import {useEffect, useState, useCallback, useRef} from "react";
import {ChatModel} from "@/app/api/chat";
import {useNavigate} from "react-router-dom";
import {ChatRequests} from "@/app/api/chat/requests";
import {SearchParamsKey, useUrlParams} from "@/app/utils/use_search_params";
import {MessagesRequests} from "@/app/api/messages/requests";
import {MessageModel, SendMessageRequestModel} from "@/app/api/messages";
import Cookies from "js-cookie";
import {CookiesKey} from "@/app/utils/cookies";
import {NearbyUsersModel, UserRequests} from "@/app/api/user";
import useChatSocket from "@/app/services/chat_soket";

const PAGE_SIZE = 10;

export interface ScreenState {
  hasError?: string;
  users?: NearbyUsersModel[];
  loading: boolean;
  loadingMore: boolean;
  page: number;
  isAll: boolean;
  query: string;
}

const useAddMember = () => {

  const loadingRef = useRef(false);

  const [state, setState] = useState<ScreenState>({
    hasError: undefined,
    users: undefined,
    loading: false,
    loadingMore: false,
    page: 0,
    isAll: false,
    query: ''
  });

  const updateState = useCallback((updates: Partial<ScreenState> | ((prev: ScreenState) => ScreenState)) => {
    setState((prev) => typeof updates === 'function' ? updates(prev) : ({...prev, ...updates}));
  }, []);

  const searchUsers = useCallback(async (query: string) => {
    const trimmed = query.trim();
    if (loadingRef.current) return;
    try {
      loadingRef.current = true;
      updateState({loading: true, query: trimmed, page: 0, isAll: false});
      const response = await UserRequests.searchUsers(trimmed, 0);
      updateState({
        users: response,
        loading: false,
        page: response.length < PAGE_SIZE ? 0 : 1,
        isAll: response.length < PAGE_SIZE
      });
    } catch (error: any) {
      updateState({hasError: error.message, loading: false});
    } finally {
      loadingRef.current = false;
    }
  }, [updateState]);

  const loadMore = useCallback(async () => {
    if (state.loadingMore || state.loading || state.isAll || loadingRef.current) return;
    try {
      loadingRef.current = true;
      updateState({loadingMore: true});
      const response = await UserRequests.searchUsers(state.query, state.page);
      updateState(prev => {
        const isAll = response.length < PAGE_SIZE;
        return {
          ...prev,
          loadingMore: false,
          users: [...(prev.users ?? []), ...response],
          page: prev.page + 1,
          isAll
        };
      });
    } catch (error: any) {
      updateState({hasError: error.message, loadingMore: false});
    } finally {
      loadingRef.current = false;
    }
  }, [state.loadingMore, state.loading, state.isAll, state.query, state.page, updateState]);

  return {
    state,
    searchUsers,
    loadMore
  };
};

export default useAddMember;